import { cn } from '#/utils/cn';
import { formatCurrency } from '#/utils/format';

import { NoData } from './NoData';

interface RangeBarProps {
  /** Low end of the range. Renders a dash if missing. */
  low: number | null | undefined;
  /** High end of the range. Renders a dash if missing. */
  high: number | null | undefined;
  /** Current value, positioned by value. Renders a dash if missing. */
  current: number | null | undefined;
  /** Accessible name describing what the range represents, e.g. "52-week price range". */
  label?: string;
  className?: string;
}

export function RangeBar({
  low,
  high,
  current,
  label,
  className,
}: RangeBarProps) {
  // If nothing to plot without both bounds and a current value return a DASH to
  // be consistent with other value fallbacks
  if (low == null || high == null || current == null) {
    return <NoData />;
  }

  const diff = high - low;
  // Center the marker when the range has no width; otherwise clamp into [0, 100].
  const ratio = diff > 0 ? (current - low) / diff : 0.5;
  const percent = Math.min(100, Math.max(0, ratio * 100));

  // ARIA requires aria-valuenow within [min, max]; clamp it the same way we do the marker
  const clampedNow = Math.min(high, Math.max(low, current));

  return (
    <div
      className={cn('flow flow-8', className)}
      role="meter"
      aria-valuemin={low}
      aria-valuemax={high}
      aria-valuenow={clampedNow}
      aria-valuetext={formatCurrency(current)}
      aria-label={label}
    >
      {/* low/high */}
      <div className="text-static-14 text-slate-90 flex justify-between font-semibold">
        <span>{formatCurrency(low)}</span>
        <span>{formatCurrency(high)}</span>
      </div>

      {/* track + marker */}
      <div className="bg-blue-30 relative h-12 rounded-full">
        <div
          className="bg-steel-30 absolute top-1/2 size-12 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-slate-100"
          style={{ left: `${percent}%` }}
        />
      </div>

      {/* current */}
      <div className="text-static-12 text-fg relative h-20">
        <span
          className="absolute -translate-x-1/2 whitespace-nowrap"
          style={{ left: `${percent}%` }}
        >
          {formatCurrency(current)}
        </span>
      </div>
    </div>
  );
}
